import { BaseLayoutManager, detectMode, detectOrientation } from '@bn-games/layout-core';
import type { LayoutConfig, LayoutMode } from './types.js';

/**
 * Desktop reserves chrome around the board (menu padding, drop shadow);
 * mobile fills the viewport with a small inset that respects safe areas.
 */
const DESKTOP_CANVAS_MARGIN_PX = 48;
const MOBILE_CANVAS_MARGIN_PX = 0;

function computeCanvasDisplaySize(
  mode: LayoutMode,
  viewportWidth: number,
  viewportHeight: number,
): number {
  const margin = mode === 'desktop' ? DESKTOP_CANVAS_MARGIN_PX : MOBILE_CANVAS_MARGIN_PX;
  const usableW = Math.max(0, viewportWidth - margin * 2);
  const usableH = Math.max(0, viewportHeight - margin * 2);
  // Square canvas — fit the smaller axis to preserve aspect ratio.
  return Math.max(160, Math.floor(Math.min(usableW, usableH)));
}

export class LayoutManager extends BaseLayoutManager<LayoutConfig> {
  protected compute(): LayoutConfig {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const mode = detectMode(viewportWidth);
    const orientation = detectOrientation(viewportWidth, viewportHeight);
    const canvasDisplaySize = computeCanvasDisplaySize(mode, viewportWidth, viewportHeight);
    const devicePixelRatio = window.devicePixelRatio || 1;
    return { mode, orientation, viewportWidth, viewportHeight, canvasDisplaySize, devicePixelRatio };
  }

  protected equals(a: LayoutConfig, b: LayoutConfig): boolean {
    return (
      a.mode === b.mode &&
      a.orientation === b.orientation &&
      a.viewportWidth === b.viewportWidth &&
      a.viewportHeight === b.viewportHeight &&
      a.canvasDisplaySize === b.canvasDisplaySize &&
      a.devicePixelRatio === b.devicePixelRatio
    );
  }
}
